import React, { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import Button from '../../../../../components/ComponentsV1/Button';
import InputV1 from '../../../../../components/ComponentsV1/InputV1';
import { useClient } from '../../../../../api/api-client';
import {
  IVA_POLICIES_ENDPOINTS,
  LOOKUP_TYPE,
  LookupTypeOptions,
  LOOKUP_TYPE_ID_LABEL,
  LOOKUP_TYPE_ID_PLACEHOLDER,
  ROLLOUT_GROUP,
  RolloutGroupOptions,
  STEP_GUIDELINE_TYPE,
  StepGuidelineTypeOptions,
  INPUT_MODE,
  inputClassName,
  textAreaClassName,
} from '../constants';
import {
  directTextToPayload,
  stepsToPayload,
  parseGuidelineImport,
  parseGuidelineObject,
  readErrorMessage,
} from '../helpers';
import GuidelineBuilder from './GuidelineBuilder';
import IvaTabBar from './IvaTabBar';

const emptyBuilder = () => ({
  mode: INPUT_MODE.DIRECT,
  directText: '',
  steps: [],
});

const buildInitialState = (initial) => {
  const parsed = initial ? parseGuidelineObject(initial) : null;
  return {
    lookupType: parsed?.lookupType || LOOKUP_TYPE.CATEGORY,
    lookupId: parsed?.lookupId == null ? '' : String(parsed.lookupId),
    rolloutGroup: parsed?.rolloutGroup || ROLLOUT_GROUP.TARGET,
    stepType: STEP_GUIDELINE_TYPE.SUMMARY,
    summary: parsed?.summaryBuilder || emptyBuilder(),
    verification: parsed?.verificationBuilder || emptyBuilder(),
  };
};

const builderToPayload = (builder) =>
  builder.mode === INPUT_MODE.DIRECT
    ? directTextToPayload(builder.directText)
    : stepsToPayload(builder.steps);

const GuidelineForm = ({ complaintType, initial, onSaved }) => {
  const client = useClient();
  const updatedBy = useSelector(
    (state) => state.authState?.userDetails?.crm_user_id,
  );
  const [form, setForm] = useState(buildInitialState(initial));
  const [isSaving, setIsSaving] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const activeBuilderKey =
    form.stepType === STEP_GUIDELINE_TYPE.SUMMARY ? 'summary' : 'verification';

  const handleApplyJson = useCallback(() => {
    try {
      const parsed = parseGuidelineImport(jsonText);
      setForm((prev) => ({
        ...prev,
        lookupType: parsed.lookupType || prev.lookupType,
        lookupId: parsed.lookupId == null ? prev.lookupId : String(parsed.lookupId),
        rolloutGroup: parsed.rolloutGroup || prev.rolloutGroup,
        summary: parsed.summaryBuilder || prev.summary,
        verification: parsed.verificationBuilder || prev.verification,
      }));
      toast.success('JSON parsed — both guideline types populated');
      setShowJson(false);
    } catch (e) {
      toast.error(`Invalid JSON: ${e.message}`);
    }
  }, [jsonText]);

  const handleSave = useCallback(async () => {
    if (!complaintType.trim()) {
      toast.error('Complaint type is required');
      return;
    }
    if (!form.lookupId.trim()) {
      toast.error('Lookup id is required');
      return;
    }

    const payload = {
      complaint_type: complaintType.trim(),
      lookup_type: form.lookupType,
      lookup_id: form.lookupId.trim(),
      rollout_group: form.rolloutGroup,
      media_summarization_guidelines: builderToPayload(form.summary),
      step_by_step_verification_guidelines: builderToPayload(form.verification),
      updated_by: String(updatedBy || ''),
    };

    setIsSaving(true);
    try {
      await client(IVA_POLICIES_ENDPOINTS.CREATE_OR_UPDATE, { data: payload });
      toast.success('Guideline saved successfully');
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(readErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  }, [client, complaintType, form, onSaved]);

  return (
    <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-border tw-border-solid tw-border-grey-200 tw-flex tw-flex-col tw-gap-6'>
      <div className='tw-flex tw-items-center tw-justify-between'>
        <div className='tw-text-[1.6rem] tw-font-medium tw-text-grey-900'>
          {initial ? 'Update Guideline' : 'Create Guideline'}
        </div>
        <Button
          title={showJson ? 'Close JSON' : 'Paste JSON'}
          type='secondary'
          onClick={() => setShowJson((prev) => !prev)}
        />
      </div>

      {showJson && (
        <div className='tw-flex tw-flex-col tw-gap-3 tw-bg-grey-100/60 tw-p-5 tw-rounded-xl'>
          <textarea
            className={textAreaClassName}
            placeholder='Paste a guideline JSON payload here'
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
          />
          <div>
            <Button title='Parse & Apply' onClick={handleApplyJson} />
          </div>
        </div>
      )}

      <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6'>
        <div className='tw-flex tw-flex-col tw-gap-2'>
          <label className='tw-text-[1.4rem] tw-font-medium tw-text-grey-800'>
            Lookup Type
          </label>
          <InputV1
            options={LookupTypeOptions}
            value={form.lookupType}
            placeholder='Select lookup type'
            isSearchable
            closeOnSelect
            onSelect={(opt) => setField('lookupType', opt?.value)}
          />
        </div>
        <div className='tw-flex tw-flex-col tw-gap-2'>
          <label className='tw-text-[1.4rem] tw-font-medium tw-text-grey-800'>
            {LOOKUP_TYPE_ID_LABEL[form.lookupType] || 'Lookup Id'}
          </label>
          <input
            className={inputClassName}
            placeholder={LOOKUP_TYPE_ID_PLACEHOLDER[form.lookupType] || 'e.g. 8058'}
            value={form.lookupId}
            autoComplete='off'
            onChange={(e) => setField('lookupId', e.target.value)}
          />
        </div>
        <div className='tw-flex tw-flex-col tw-gap-2'>
          <label className='tw-text-[1.4rem] tw-font-medium tw-text-grey-800'>
            Rollout Group
          </label>
          <InputV1
            options={RolloutGroupOptions}
            value={form.rolloutGroup}
            placeholder='Select rollout group'
            isSearchable
            closeOnSelect
            onSelect={(opt) => setField('rolloutGroup', opt?.value)}
          />
        </div>
      </div>

      <div className='tw-flex tw-flex-col tw-gap-4 tw-border-0 tw-border-t tw-border-solid tw-border-grey-200 tw-pt-6'>
        <label className='tw-text-[1.4rem] tw-font-medium tw-text-grey-800'>
          Guideline Type
        </label>
        <IvaTabBar
          tabs={StepGuidelineTypeOptions.map((opt) => ({
            key: opt.value,
            title: opt.label,
          }))}
          activeKey={form.stepType}
          onChange={(value) => setField('stepType', value)}
        />

        <div className='tw-flex tw-items-center tw-gap-2 tw-bg-base-green/10 tw-border tw-border-solid tw-border-base-green/40 tw-rounded-lg tw-px-4 tw-py-3'>
          <span className='tw-text-[1.4rem] tw-font-medium tw-text-base-green'>
            Editing:{' '}
            {form.stepType === STEP_GUIDELINE_TYPE.SUMMARY
              ? 'Summary Guidelines'
              : 'Verification Guidelines'}
          </span>
        </div>

        <GuidelineBuilder
          state={form[activeBuilderKey]}
          onChange={(next) => setField(activeBuilderKey, next)}
        />
      </div>

      <div>
        <Button
          title={initial ? 'Update Guideline' : 'Create Guideline'}
          onClick={handleSave}
          disabled={isSaving}
          loading={isSaving}
        />
      </div>
    </div>
  );
};

export default GuidelineForm;
